/**
 * lockerPreview — the Locker's live preview: the chosen skin sprite moving on
 * a figure-8 with its trail drawn by the *same* code the match uses
 * (TrailHistory → resolveTrailDefinition → getTrailStyle(def.style).draw), so
 * what you see is what you get. Mirrors PixiEntityRenderer's body: shadow r40
 * @ .35, body 80×80 anchored centre, rotation = heading + π/2, and the
 * equipped backpack under the body placed by the same backpackLayout().
 *
 * A separate PIXI.Application: PixiTrailRenderer is a singleton bound to the
 * game's trails layer and cannot be re-inited here. Fixed size (the panel may
 * be display:none — never resizeTo). Own textures (skip-cache) because
 * PixiRenderer destroys the shared cached ones on context restore/destroy.
 * The only locker module that imports pixi.
 */
import * as PIXI from 'pixi.js';
import { TrailHistory } from '../core/TrailHistory.js';
import { getTrailStyle, resolveTrailDefinition, toColor } from '../rendering/pixi/trailStyles.js';
import { resolveBackpack, backpackLayout } from '../core/backpacks.js';

const PLAYER_RADIUS = 40;   // PixiEntityRenderer.PLAYER_RADIUS / SHADOW_RADIUS
const SPEED = 1.6;          // rad/s along the figure-8 → ~1.9–3.3 px/frame at 60 fps in a 320×200 panel

export async function createLockerPreview(host, { width = 320, height = 200 } = {}) {
  const app = new PIXI.Application();
  await app.init({ width, height, backgroundAlpha: 0, antialias: true, resolution: window.devicePixelRatio || 1, autoDensity: true });
  host.innerHTML = '';
  host.appendChild(app.canvas);

  const trailG = new PIXI.Graphics();
  const body = new PIXI.Container();
  const shadow = new PIXI.Graphics().circle(0, 0, PLAYER_RADIUS).fill({ color: 0x000000, alpha: 0.35 });
  const rot = new PIXI.Container();
  const sprite = new PIXI.Sprite();
  sprite.anchor.set(0.5, 0.5);
  sprite.width = sprite.height = PLAYER_RADIUS * 2;
  const backpack = new PIXI.Sprite();
  backpack.anchor.set(0.5, 0.5);
  backpack.visible = false;
  rot.addChild(backpack, sprite);   // backpack first: the body covers its top half
  body.addChild(shadow, rot);
  app.stage.addChild(trailG, body);

  // The preview moves ~2–3 px/frame, a third of a match player's ~6; a 2 px gate
  // (vs. the default 6) gives the same samples-per-pixel, so the trail is as dense as in-game.
  const history = new TrailHistory({ minDistance: 2 });
  let loadout = { skin: 'default', trail: 'default', backpack: null };
  let cosmeticsConfig = null;
  let texture = null;
  let lastImg = null;
  let backpackTexture = null;
  let lastBackpackImg;   // undefined, not null: the first setBackpackImage(null) still runs
  const scale = Math.min(1, height / 260);          // keep the 80 px body inside a 200 px panel
  body.scale.set(scale);
  const cx = width / 2, cy = height / 2, A = width * 0.32, B = height * 0.36;

  app.ticker.add(() => {
    const now = performance.now();
    const t = (now / 1000) * SPEED;
    const x = cx + A * Math.sin(t);
    const y = cy + (B * Math.sin(2 * t)) / 2;
    const dx = A * Math.cos(t), dy = B * Math.cos(2 * t);
    body.position.set(x, y);
    rot.rotation = Math.atan2(dy, dx) + Math.PI / 2;

    history.record('me', x, y, now, true);
    history.prune(now);
    trailG.clear();
    const samples = history.samplesFor('me');
    if (samples.length < 2) return;
    const def = resolveTrailDefinition({ cosmetics: { equippedTrail: loadout.trail } }, cosmeticsConfig);
    const style = getTrailStyle(def.style) || getTrailStyle('ribbon');
    style.draw(trailG, samples, {
      now, lifeMs: def.lifeMs, color: toColor(def.color),
      secondaryColor: def.secondaryColor != null ? toColor(def.secondaryColor) : null,
      width: def.width * scale, alpha: def.alpha, isLocal: true
    });
  });
  app.ticker.stop();

  function placeBackpack() {
    const entry = resolveBackpack(loadout.backpack, cosmeticsConfig);
    if (!entry || !backpackTexture) { backpack.visible = false; return; }
    const { width, height, offsetY } = backpackLayout(entry);
    backpack.width = width;
    backpack.height = height;
    backpack.y = offsetY;
    backpack.visible = true;
  }

  return {
    setLoadout(state, config) { loadout = { ...loadout, ...state }; cosmeticsConfig = config; placeBackpack(); },
    setSkinImage(img) {
      if (img === lastImg) return;   // LockerScreen re-previews on every hover; the skin rarely changes
      const previous = texture;
      if (img && img.complete && img.naturalWidth > 0) {
        texture = PIXI.Texture.from(img, true);   // skipCache: this texture is ours to destroy
        sprite.texture = texture;
        sprite.width = sprite.height = PLAYER_RADIUS * 2;
        sprite.visible = true;
      } else {
        texture = null;
        sprite.texture = PIXI.Texture.EMPTY;
        sprite.visible = false;
      }
      lastImg = img;
      if (previous) previous.destroy(true);   // repoint first: the sprite never holds a destroyed texture
    },
    /** The equipped backpack's image, or null for no backpack. Same texture ownership as setSkinImage. */
    setBackpackImage(img) {
      if (img === lastBackpackImg) return;
      const previous = backpackTexture;
      if (img && img.complete && img.naturalWidth > 0) {
        backpackTexture = PIXI.Texture.from(img, true);
        backpack.texture = backpackTexture;
      } else {
        backpackTexture = null;
        backpack.texture = PIXI.Texture.EMPTY;
      }
      lastBackpackImg = img;
      placeBackpack();
      if (previous) previous.destroy(true);
    },
    start() { history.clear(); app.ticker.start(); },
    stop() { app.ticker.stop(); },
    destroy() {
      app.ticker.stop();
      if (texture) texture.destroy(true);
      if (backpackTexture) backpackTexture.destroy(true);
      app.destroy(true, { children: true });
    }
  };
}
